/**
 * Core Dialog / Modal Component
 *
 * Accessible modal dialog with size variants, header/footer composition,
 * close button, backdrop click control, and scroll behavior.
 * Built on MUI Dialog for accessibility and keyboard handling.
 *
 * @example
 *   <Dialog open={isOpen} onClose={close} title="Edit User" size="md">
 *     <p>Dialog content...</p>
 *     <DialogFooter>
 *       <Button variant="ghost" onClick={close}>Cancel</Button>
 *       <Button onClick={save}>Save</Button>
 *     </DialogFooter>
 *   </Dialog>
 */
import React from 'react';
import {
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DialogProps {
  /** Open state */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Title text */
  title?: React.ReactNode;
  /** Subtitle/description under title */
  subtitle?: string;
  /** Icon before title */
  icon?: React.ReactNode;
  /** Dialog content */
  children: React.ReactNode;
  /** Size preset */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
  /** Disable close on backdrop click */
  disableBackdropClose?: boolean;
  /** Disable close on Escape key */
  disableEscapeClose?: boolean;
  /** Hide the close (X) button */
  hideCloseButton?: boolean;
  /** Scroll behavior */
  scroll?: 'body' | 'paper';
  /** Full width */
  fullWidth?: boolean;
  /** Custom class for dialog paper */
  className?: string;
  /** Dividers between title/content/actions */
  dividers?: boolean;
}

export interface DialogFooterProps {
  children: React.ReactNode;
  className?: string;
  /** Align footer buttons */
  align?: 'left' | 'center' | 'right';
}

const sizeMap: Record<string, 'xs' | 'sm' | 'md' | 'lg' | 'xl'> = {
  xs: 'xs', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl',
};

export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  size = 'sm',
  disableBackdropClose = false,
  disableEscapeClose = false,
  hideCloseButton = false,
  scroll = 'paper',
  fullWidth = true,
  className = '',
  dividers = false,
}) => (
  <MuiDialog
    open={open}
    onClose={disableBackdropClose ? undefined : onClose}
    maxWidth={size === 'fullscreen' ? false : sizeMap[size]}
    fullWidth={fullWidth}
    fullScreen={size === 'fullscreen'}
    scroll={scroll}
    disableEscapeKeyDown={disableEscapeClose}
    PaperProps={{
      className,
      sx: { borderRadius: size === 'fullscreen' ? 0 : 'var(--border-radius, 12px)' },
    }}
  >
    {(title || !hideCloseButton) && (
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pr: 6 }}>
        {icon && <span className="flex items-center mt-0.5 text-slate-400">{icon}</span>}
        <div className="flex-1 min-w-0">
          {typeof title === 'string' ? (
            <Typography variant="h6" fontWeight={700} noWrap>{title}</Typography>
          ) : title}
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>
          )}
        </div>
        {!hideCloseButton && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12 }}
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </IconButton>
        )}
      </DialogTitle>
    )}
    <DialogContent dividers={dividers} sx={{ pt: title ? 1 : 2 }}>
      {children}
    </DialogContent>
  </MuiDialog>
);

export const DialogFooter: React.FC<DialogFooterProps> = ({
  children,
  className = '',
  align = 'right',
}) => (
  <DialogActions
    className={className}
    sx={{
      px: 3, py: 2,
      justifyContent: align === 'left' ? 'flex-start' : align === 'center' ? 'center' : 'flex-end',
      gap: 1,
    }}
  >
    {children}
  </DialogActions>
);

export default Dialog;
